/**
 * Notetaker platform support.
 *
 * The Ahoy bot can only join meetings on the platforms Recall.ai supports for
 * us. This list MIRRORS `PLATFORM_URL_FRAGMENTS` in
 * `apps/recall_sync/services/scheduler.py` — the backend filter that decides
 * whether a meeting becomes a bot candidate at all.
 *
 * Keep the two in sync. A meeting the backend silently drops must never be
 * offered a "Record this meeting" control here: the user turns it on, gets a
 * success toast, and no bot ever arrives.
 */

/** Human-readable platform names, for user-facing copy. */
export const NOTETAKER_SUPPORTED_PLATFORMS_SENTENCE =
  'Zoom, Google Meet, and Microsoft Teams';

const SUPPORTED_URL_FRAGMENTS = [
  'zoom.us',
  'meet.google.com',
  'teams.microsoft.com',
  'teams.live.com',
] as const;

/**
 * Base used to resolve a protocol-relative meeting link before reading its host.
 *
 * The backend parses with Python's `urlparse`, which reads the authority from a
 * protocol-relative link like `//teams.microsoft.com/...` (host
 * `teams.microsoft.com`) and treats it as supported. `new URL('//host/...')`
 * with no base throws, so without a base the frontend would reject a link the
 * backend schedules — the exact drift the mirror must avoid. We therefore
 * resolve a scheme-less link against this base to read its host. The reserved
 * `.invalid` TLD (RFC 6761) can never be a real meeting host, so it can never
 * spoof a supported platform.
 *
 * The base is reached only AFTER a link has passed the {@link STRICT_AUTHORITY}
 * gate, so it never sees the malformed forms `new URL` would otherwise coerce
 * onto a supported host. `///teams.microsoft.com/...` (or `////...`, or
 * backslash variants) inherit the WHATWG "special authority ignore slashes"
 * rule from a special-scheme base and collapse to host `teams.microsoft.com` —
 * but Python's `urlparse` reads no host for those, so the backend drops them.
 * Rejecting everything but a strict `//host` marker before parsing keeps the
 * two parsers in step.
 */
const RELATIVE_LINK_BASE = 'https://notetaker-link-base.invalid';

/**
 * A strict authority marker: exactly two leading slashes followed by a host
 * character. This is the ONLY form Python's `urlparse` reads a host from, so —
 * after stripping an optional `scheme:` — it is the single gate both the
 * protocol-relative (`//host/...`) and the absolute (`scheme://host/...`) cases
 * pass through. `///...` (empty authority), `////...`, and backslash variants
 * (`//\\...`, `/\\/...`) all yield no usable host on the backend, so the
 * negative lookahead rejects them here in step.
 */
const STRICT_AUTHORITY = /^\/\/(?![/\\])/;

/**
 * An optional leading `scheme:` prefix, matched exactly as Python's `urlparse`
 * detects a scheme (an ASCII letter followed by letters, digits, `+`, `-`, or
 * `.`, up to the first `:`). Stripping it lets us apply the same
 * {@link STRICT_AUTHORITY} test to `scheme://host` and bare `//host` alike:
 * `urlparse` only reads a host when a `//` authority follows the (optional)
 * scheme, whereas `new URL` invents one for a special scheme even when the
 * `//` is missing or malformed (`https:host`, `https:/\\host`, `https:///host`).
 */
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Whether the authority (the `//host` segment, as Python's `urlparse` delimits
 * it) contains a backslash.
 *
 * `urlparse` treats `\` as an ordinary authority character, so
 * `https://zoom.us\evil.com/...` keeps host `zoom.us\evil.com`, and
 * `https://zoom.us\@evil.com/...` reads host `evil.com` — neither is a
 * supported platform, so the backend drops the link. The WHATWG parser behind
 * `new URL` instead treats `\` as `/` for special schemes, collapsing the
 * authority back to the supported host and reporting it as supported. Rejecting
 * a backslashed authority up front keeps the frontend in step with the backend,
 * so the "Record this meeting" control is never offered for a link no bot joins.
 *
 * The authority is delimited exactly as `urlparse` does it — from after the
 * `//` to the next `/`, `?`, or `#` — so a backslash in the path or query (which
 * changes neither parser's host) does not reject an otherwise-supported link.
 */
function authorityHasBackslash(url: string): boolean {
  const afterScheme = url.replace(SCHEME_PREFIX, '');
  if (!afterScheme.startsWith('//')) {
    return false;
  }
  const authority = afterScheme.slice(2).split(/[/?#]/, 1)[0];
  return authority.includes('\\');
}

/**
 * Whether the Ahoy bot can join a meeting at this URL.
 *
 * Matches on the URL *host*, not a substring of the whole URL: a fragment like
 * `teams.microsoft.com` appearing in a path or query, or a look-alike host such
 * as `teams.microsoft.com.evil.com`, must NOT count as supported (mirrors the
 * backend `_is_supported_platform`). A fragment matches when it is the host
 * exactly or a dot-delimited suffix of it (Zoom uses regional subdomains like
 * `us06web.zoom.us`).
 *
 * Protocol-relative links (`//teams.microsoft.com/...`) are accepted when their
 * host is supported, matching the backend (see RELATIVE_LINK_BASE).
 *
 * Returns false for an absent or empty link — a meeting with no join URL
 * (dial-in only, in person) has nothing for a bot to join either — and for a
 * link that does not parse as a URL.
 */
export function isNotetakerSupportedMeetingUrl(
  meetingUrl: string | null | undefined
): boolean {
  if (meetingUrl == null || meetingUrl.trim() === '') {
    return false;
  }
  const trimmed = meetingUrl.trim();
  // `new URL` collapses backslashes in a special-scheme authority to slashes,
  // reading a supported host where Python's `urlparse` reads an unsupported one.
  // Reject a backslashed authority before parsing to stay in step (see above).
  if (authorityHasBackslash(trimmed)) {
    return false;
  }
  // Python's `urlparse` reads a host only when a strict `//host` authority
  // follows the optional scheme. `new URL` instead invents a host for a special
  // scheme even without it — `https:teams.microsoft.com/...`, `https:///...`,
  // `https:/\\...` all resolve to a supported host it would never join. Require
  // the same `//host` marker the backend does, for both the absolute
  // (`scheme://host`) and protocol-relative (`//host`) forms, and reject
  // everything else before parsing.
  const hasScheme = SCHEME_PREFIX.test(trimmed);
  const afterScheme = trimmed.replace(SCHEME_PREFIX, '');
  if (!STRICT_AUTHORITY.test(afterScheme)) {
    return false;
  }
  let hostname: string;
  try {
    // A protocol-relative link has no scheme, so resolve it against the base to
    // read its host (see RELATIVE_LINK_BASE); an absolute link is parsed as-is.
    const parsed = hasScheme
      ? new URL(trimmed)
      : new URL(trimmed, RELATIVE_LINK_BASE);
    // `.rstrip(".")` on the backend strips the entire trailing-dot run, so a
    // host like `teams.microsoft.com..` must lose ALL trailing dots here too.
    hostname = parsed.hostname.toLowerCase().replace(/\.+$/, '');
  } catch {
    return false;
  }
  return SUPPORTED_URL_FRAGMENTS.some(
    fragment => hostname === fragment || hostname.endsWith(`.${fragment}`)
  );
}
